import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowUpRight, Lock } from "lucide-react";
import PageHero from "@/components/site/PageHero";
import ShaderBackground from "@/components/site/ShaderBackground";
import { Pill, Reveal } from "@/components/site/Reveal";
import { PillButton } from "@/components/site/Kit";

// A private page for one audience. The words come from the server only after
// the right code (cms/pages.mjs); this file holds the layout and nothing else.
async function call(path, payload) {
  const response = await fetch(path, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  const body = await response.json().catch(() => ({}));
  if (!response.ok)
    throw Object.assign(new Error(body.error || ""), {
      status: response.status,
    });
  return body;
}

function Door({ onCode }) {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError("");
    try {
      await onCode(code);
    } catch (err) {
      setError(
        err.status === 401 || err.status === 429
          ? err.message
          : "That did not work. Try again in a moment.",
      );
      setBusy(false);
    }
  };
  return (
    <section
      className="relative flex min-h-screen items-center overflow-hidden bg-black px-5 pb-16 pt-28 text-white"
      data-testid="private-door"
    >
      <ShaderBackground dark strength={0.2} lines={12} fadeLeft={0} />
      <div className="relative z-10 mx-auto w-full max-w-[1400px]">
        <Pill white>
          <Lock size={11} className="mr-1.5" /> Private
        </Pill>
        <h1 className="nm-title mt-6 text-[48px] leading-[0.98] sm:text-[84px]">
          Have an invitation code?
        </h1>
        <form
          className="mt-10 flex max-w-[520px] flex-col gap-2 sm:flex-row"
          onSubmit={submit}
        >
          <input
            aria-label="Invitation code"
            placeholder="Invitation code"
            autoComplete="off"
            autoCapitalize="characters"
            spellCheck={false}
            value={code}
            onChange={(e) => setCode(e.target.value)}
            data-testid="private-code"
            className="h-12 w-full min-w-0 shrink-0 rounded-full bg-white/10 px-6 text-[16px] uppercase tracking-[0.12em] text-white outline-none ring-1 ring-white/15 placeholder:normal-case placeholder:tracking-normal placeholder:text-white/30 focus:ring-white/60 sm:w-auto sm:flex-1"
          />
          <button
            type="submit"
            disabled={busy || !code.trim()}
            data-testid="private-open"
            className="h-12 shrink-0 rounded-full bg-white px-7 text-[14px] font-medium text-black transition-colors hover:bg-[#e6e6e6] disabled:opacity-60"
          >
            {busy ? "Opening…" : "Open"}
          </button>
        </form>
        {error && (
          <p
            role="alert"
            className="mt-4 text-[14px] text-red-300"
            data-testid="private-error"
          >
            {error}
          </p>
        )}
        <p className="mt-10 max-w-[460px] text-[13px] leading-[1.45] text-white/40">
          Don't have one? Write to hello@nomus.work.
        </p>
      </div>
    </section>
  );
}

const SECTION = "px-5 pt-16 lg:pt-28";
const WRAP = "mx-auto max-w-[1400px]";
const H2 = "nm-title text-[30px] leading-[1.08] sm:text-[48px]";
const BODY = "text-[18px] leading-[1.45] opacity-60 sm:text-[20px]";
const two = (i) => String(i + 1).padStart(2, "0");

function Proposal({ page, onLeave }) {
  const {
    hero,
    facts,
    why,
    method,
    test,
    weeks,
    ladder,
    gains,
    examples,
    format,
    start,
    who,
    close,
  } = page;
  return (
    <div data-testid="private-page">
      <PageHero label={page.label} title={hero.title} text={hero.text} />

      <section className="px-5 pt-12 lg:pt-16">
        <dl className={`${WRAP} grid grid-cols-2 gap-3 lg:grid-cols-4`}>
          {facts.map(([k, v]) => (
            <div key={k} className="rounded-[20px] bg-[#f7f7f7] p-6">
              <dt className="text-[12px] opacity-45">{k}</dt>
              <dd className="nm-display mt-2 text-[24px] leading-[1.05] tracking-[-0.02em] sm:text-[32px]">
                {v}
              </dd>
            </div>
          ))}
        </dl>
      </section>

      <section className={SECTION}>
        <Reveal className={`${WRAP} grid gap-8 lg:grid-cols-12`}>
          <h2 className="nm-title text-[34px] leading-[1.04] sm:text-[60px] lg:col-span-7">
            {why.title}
          </h2>
          <div className="space-y-5 lg:col-span-5 lg:pt-3">
            {why.text.map((p) => (
              <p key={p} className={BODY}>
                {p}
              </p>
            ))}
          </div>
        </Reveal>
      </section>

      <section className={SECTION}>
        <div className={WRAP}>
          <Reveal>
            <h2 className={H2}>{method.title}</h2>
          </Reveal>
          <ol className="mt-10 grid gap-3 sm:grid-cols-2 lg:mt-14 lg:grid-cols-4">
            {method.steps.map(([name, line], i) => (
              <Reveal
                as="li"
                key={name}
                delay={i * 70}
                className="flex min-h-[220px] flex-col justify-between rounded-[28px] bg-[#f7f7f7] p-7"
              >
                <span className="text-[13px] tabular-nums opacity-40">
                  {two(i)}
                </span>
                <div>
                  <p className="nm-title text-[36px] leading-none sm:text-[44px]">
                    {name}
                  </p>
                  <p className="mt-4 text-[17px] leading-[1.4] opacity-60">
                    {line}
                  </p>
                </div>
              </Reveal>
            ))}
          </ol>
          <p className="mt-6 max-w-[720px] text-[16px] leading-[1.45] opacity-60">
            {method.note}
          </p>
        </div>
      </section>

      <section className={SECTION}>
        <Reveal
          className={`${WRAP} rounded-[28px] bg-black p-7 text-white sm:p-12`}
        >
          <div className="grid gap-8 lg:grid-cols-12">
            <div className="lg:col-span-5">
              <h2 className="nm-title text-[32px] leading-[1.04] sm:text-[52px]">
                {test.title}
              </h2>
              <p className="mt-5 max-w-[460px] text-[18px] leading-[1.45] text-white/65">
                {test.text}
              </p>
            </div>
            <ol className="grid gap-x-8 sm:grid-cols-2 lg:col-span-7">
              {test.questions.map(([q, a], i) => (
                <li key={q} className="border-t border-white/15 py-6">
                  <span className="text-[13px] tabular-nums text-white/40">
                    {two(i)}
                  </span>
                  <p className="mt-2 text-[21px] leading-[1.2] tracking-[-0.02em] sm:text-[24px]">
                    {q}
                  </p>
                  <p className="mt-2 text-[16px] leading-[1.4] text-white/55">
                    {a}
                  </p>
                </li>
              ))}
            </ol>
          </div>
          <p className="mt-8 max-w-[720px] text-[16px] leading-[1.45] text-white/55">
            {test.note}
          </p>
        </Reveal>
      </section>

      <section className={SECTION} data-testid="private-weeks">
        <div className={WRAP}>
          <Reveal>
            <h2 className={H2}>The four weeks.</h2>
          </Reveal>
          <ol className="mt-10 border-t border-black/10 lg:mt-14">
            {weeks.map((w, i) => (
              <Reveal
                as="li"
                key={w.when}
                delay={Math.min(i, 4) * 60}
                className="grid gap-x-8 gap-y-3 border-b border-black/10 py-8 sm:py-10 lg:grid-cols-12"
              >
                <div className="lg:col-span-3">
                  <p className="text-[13px] opacity-45">{w.when}</p>
                  <p className="nm-display mt-1 text-[34px] leading-none tracking-[-0.02em] sm:text-[44px]">
                    {w.title}
                  </p>
                </div>
                <div className="lg:col-span-7">
                  <p className="text-[22px] leading-[1.15] tracking-[-0.02em] sm:text-[28px]">
                    {w.line}
                  </p>
                  <ul className="mt-5 space-y-2.5">
                    {w.learn.map((l) => (
                      <li
                        key={l}
                        className="relative pl-5 text-[16px] leading-[1.45] opacity-70 sm:text-[17px]"
                      >
                        <span
                          aria-hidden="true"
                          className="absolute left-0 top-[0.62em] h-[5px] w-[5px] rounded-full bg-black/40"
                        />
                        {l}
                      </li>
                    ))}
                  </ul>
                  <div className="mt-6 rounded-[18px] bg-[#f7f7f7] px-5 py-4">
                    <p className="text-[11px] uppercase tracking-[0.08em] opacity-45">
                      You leave with
                    </p>
                    <p className="mt-1 text-[17px] leading-[1.4] sm:text-[18px]">
                      {w.leave}
                    </p>
                  </div>
                </div>
                <p className="text-[14px] lg:col-span-2 lg:pt-2 lg:text-right">
                  <span className="block text-[11px] uppercase tracking-[0.08em] opacity-40">
                    Time
                  </span>
                  {w.time}
                </p>
              </Reveal>
            ))}
          </ol>
        </div>
      </section>

      <section className={SECTION} data-testid="private-ladder">
        <div className={WRAP}>
          <Reveal className="grid gap-6 lg:grid-cols-12">
            <h2 className={`${H2} lg:col-span-6`}>{ladder.title}</h2>
            <p className={`${BODY} max-w-[520px] lg:col-span-5 lg:col-start-8 lg:pt-3`}>
              {ladder.text}
            </p>
          </Reveal>
          <ol className="mt-10 grid gap-3 lg:mt-14 lg:grid-cols-4 lg:items-end">
            {ladder.rungs.map(([name, what, tools], i) => (
              <Reveal
                as="li"
                key={name}
                delay={i * 80}
                className={`flex flex-col rounded-[28px] p-7 ${
                  i === 3 ? "bg-black text-white" : "bg-[#f7f7f7]"
                } ${["lg:min-h-[260px]", "lg:min-h-[310px]", "lg:min-h-[360px]", "lg:min-h-[410px]"][i]}`}
              >
                <span className="text-[13px] tabular-nums opacity-45">
                  Step {i + 1}
                </span>
                <p className="mt-auto pt-10 text-[24px] leading-[1.1] tracking-[-0.02em] sm:text-[28px]">
                  {name}
                </p>
                <p className="mt-3 text-[16px] leading-[1.4] opacity-65">
                  {what}
                </p>
                <p className="mt-5 text-[13px] opacity-50">With: {tools}</p>
              </Reveal>
            ))}
          </ol>
        </div>
      </section>

      <section className={SECTION} data-testid="private-gains">
        <div className={WRAP}>
          <Reveal>
            <h2 className={H2}>{gains.title}</h2>
          </Reveal>
          <div className="mt-10 grid gap-3 md:grid-cols-2 lg:mt-14">
            {gains.groups.map(([head, lines], i) => (
              <Reveal
                key={head}
                delay={(i % 2) * 70}
                className={`rounded-[28px] p-7 sm:p-10 ${
                  i === 2 ? "bg-black text-white" : "bg-[#f7f7f7]"
                }`}
              >
                <h3 className="nm-title text-[26px] leading-[1.05] sm:text-[34px]">
                  {head}
                </h3>
                <ul className="mt-6 space-y-3">
                  {lines.map((line) => (
                    <li
                      key={line}
                      className={`text-[17px] leading-[1.35] sm:text-[19px] ${
                        i === 2 ? "text-white/70" : "opacity-70"
                      }`}
                    >
                      {line}
                    </li>
                  ))}
                </ul>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className={SECTION}>
        <div className={WRAP}>
          <Reveal className="grid gap-6 lg:grid-cols-12">
            <h2 className={`${H2} lg:col-span-6`}>{examples.title}</h2>
            <p className={`${BODY} max-w-[520px] lg:col-span-5 lg:col-start-8 lg:pt-3`}>
              {examples.note}
            </p>
          </Reveal>
          <ul className="mt-10 border-t border-black/10 lg:mt-14">
            {examples.items.map(([lever, job], i) => (
              <Reveal
                as="li"
                key={lever}
                delay={i * 50}
                className="grid gap-x-8 gap-y-2 border-b border-black/10 py-6 sm:py-7 lg:grid-cols-12"
              >
                <p className="text-[14px] font-medium lg:col-span-3 lg:pt-1.5">
                  {lever}
                </p>
                <p className="text-[20px] leading-[1.3] tracking-[-0.01em] sm:text-[24px] lg:col-span-9">
                  {job}
                </p>
              </Reveal>
            ))}
          </ul>
        </div>
      </section>

      <section className={SECTION}>
        <div className={`${WRAP} grid gap-3 md:grid-cols-2`}>
          {[
            ["What an advisor needs", format.need, "bg-[#f7f7f7]", "opacity-70"],
            ["What they don't need", format.without, "bg-black text-white", "text-white/70"],
          ].map(([head, lines, box, tone]) => (
            <Reveal key={head} className={`rounded-[28px] p-7 sm:p-10 ${box}`}>
              <h2 className="nm-title text-[28px] leading-[1.05] sm:text-[40px]">
                {head}
              </h2>
              <ul className="mt-8 space-y-3">
                {lines.map((line) => (
                  <li
                    key={line}
                    className={`text-[18px] leading-[1.3] sm:text-[20px] ${tone}`}
                  >
                    {line}
                  </li>
                ))}
              </ul>
            </Reveal>
          ))}
        </div>
        <div className={`${WRAP} mt-3 rounded-[28px] bg-[#ececec] p-7 sm:p-10`}>
          <Pill white>The first cohort</Pill>
          <p className="mt-5 max-w-[880px] text-[20px] leading-[1.35] tracking-[-0.01em] sm:text-[24px]">
            {format.cohort}
          </p>
          <dl className="mt-8 grid gap-6 border-t border-black/10 pt-6 sm:grid-cols-3">
            {format.rhythm.map(([k, v]) => (
              <div key={k}>
                <dt className="text-[12px] opacity-45">{k}</dt>
                <dd className="mt-1 text-[16px] leading-[1.4]">{v}</dd>
              </div>
            ))}
          </dl>
        </div>
      </section>

      <section className={SECTION}>
        <div className={WRAP}>
          <Reveal>
            <h2 className={H2}>{start.title}</h2>
          </Reveal>
          <ol className="mt-10 grid gap-x-6 border-t border-black/10 sm:grid-cols-2 lg:mt-14 lg:grid-cols-4">
            {start.steps.map(([name, text], i) => (
              <Reveal
                as="li"
                key={name}
                delay={i * 70}
                className="border-b border-black/10 py-7 lg:border-b-0"
              >
                <span className="text-[13px] tabular-nums opacity-40">
                  {two(i)}
                </span>
                <p className="mt-2 text-[22px] leading-[1.15] tracking-[-0.02em] sm:text-[26px]">
                  {name}
                </p>
                <p className="mt-2 text-[16px] leading-[1.45] opacity-60">
                  {text}
                </p>
              </Reveal>
            ))}
          </ol>
          <p className="mt-8 max-w-[720px] text-[16px] leading-[1.45] opacity-60">
            {start.note}
          </p>
        </div>
      </section>

      <section className={SECTION}>
        <Reveal
          className={`${WRAP} grid items-center gap-8 rounded-[28px] bg-[#f7f7f7] p-7 sm:p-12 lg:grid-cols-12`}
        >
          <div className="flex items-center gap-5 lg:col-span-4">
            <img
              src={who.photo}
              alt={who.name}
              className="h-20 w-20 shrink-0 rounded-full object-cover grayscale sm:h-24 sm:w-24"
            />
            <div>
              <p className="text-[13px] opacity-45">{who.title}</p>
              <p className="mt-1 text-[22px] leading-[1.1] tracking-[-0.02em]">
                {who.name}
              </p>
              <p className="text-[14px] opacity-55">{who.role}</p>
            </div>
          </div>
          <div className="lg:col-span-7 lg:col-start-6">
            <p className="text-[18px] leading-[1.45] opacity-70 sm:text-[20px]">
              {who.text}
            </p>
            <Link
              to={who.link[1]}
              className="group mt-5 inline-flex items-center gap-2 text-[15px] font-medium"
            >
              {who.link[0]}
              <ArrowUpRight
                size={15}
                className="transition-transform duration-500 group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
              />
            </Link>
          </div>
        </Reveal>
      </section>

      <section className="px-5 pb-20 pt-16 lg:pb-28 lg:pt-24">
        <div className={`${WRAP} flex flex-col gap-6 sm:flex-row sm:items-center sm:justify-between`}>
          <p className="nm-title text-[30px] leading-[1.05] sm:text-[44px]">
            {close.title}
          </p>
          <div className="flex flex-wrap gap-3">
            <PillButton href={close.call[1]} size="lg" target="_blank" rel="noreferrer">
              {close.call[0]}
            </PillButton>
            <PillButton href={close.mail[1]} size="lg" variant="light">
              {close.mail[0]}
            </PillButton>
          </div>
        </div>
        <div className={`${WRAP} mt-14`}>
          <button
            type="button"
            onClick={onLeave}
            className="nm-link text-[13px] opacity-45"
            data-testid="private-leave"
          >
            Forget this device
          </button>
        </div>
      </section>
    </div>
  );
}

export default function Private() {
  const { slug } = useParams();
  const [page, setPage] = useState(null);
  const [state, setState] = useState("checking");
  useEffect(() => {
    let live = true;
    call("/api/page", { slug })
      .then((r) => live && (setPage(r.page), setState("open")))
      .catch(() => live && setState("door"));
    return () => {
      live = false;
    };
  }, [slug]);
  if (state === "checking")
    return <section className="min-h-screen bg-black" aria-busy="true" />;
  if (state === "door" || !page)
    return (
      <Door
        onCode={async (code) => {
          const r = await call("/api/page", { slug, code });
          setPage(r.page);
          setState("open");
          window.scrollTo({ top: 0, behavior: "instant" });
        }}
      />
    );
  return (
    <Proposal
      page={page}
      onLeave={async () => {
        await call("/api/page/leave", { slug }).catch(() => {});
        setPage(null);
        setState("door");
      }}
    />
  );
}
